'use client';

import { useState } from 'react';
import { splitVisibleSupplements, supplementsShowMoreLabel } from '../../utils/supplement-visibility';
import { Stepper } from './Stepper';

/** Forme minimale exploitée par le picker — satisfaite par `SelectedSupplement` (booking) et par
 *  les suppléments du config carte cadeau. Générique pour préserver le type exact côté `onChange`. */
export interface SupplementPickerItem {
  supplement_id: number;
  name: string;
  price?: number | null;
  price_string?: string;
  duration_minutes?: number | null;
  /** Fourni par le backend (`isSupplementQuantitySelectable`) : ni durée ni salle, se compte au choix. */
  quantity_selectable?: boolean;
  selected: boolean;
  /** Quantité choisie (stepper). Absente : une par unité facturée de la ligne (`defaultQuantity`). */
  quantity?: number;
}

/** Stepper de quantité d'un supplément retenu, quand la ligne permet de la choisir. */
interface SupplementQuantityConfig {
  /** Quantité par défaut : unités facturées de la ligne (prestations × personnes). */
  defaultQuantity: number;
  max: number;
  labels: { decrease: string; increase: string };
}

interface SupplementPickerProps<T extends SupplementPickerItem> {
  supplements: T[];
  onChange: (supplements: T[]) => void;
  labels: {
    supplements_label: string;
    add_supplement: string;
    remove_supplement: string;
    show_more_one: string;
    show_more_other: string;
  };
  formatPrice: (price: number) => string;
  /** Absent : pas de choix de quantité (chaque supplément suit la règle historique). */
  quantity?: SupplementQuantityConfig;
}

export function SupplementPicker<T extends SupplementPickerItem>({
  supplements,
  onChange,
  labels,
  formatPrice,
  quantity,
}: SupplementPickerProps<T>) {
  const [expanded, setExpanded] = useState(false);
  if (supplements.length === 0) return null;
  const { visible, hiddenCount } = splitVisibleSupplements(supplements, expanded);

  // Décocher oublie la quantité choisie : recoché, le supplément repart des unités de la ligne.
  const toggle = (id: number): void => {
    onChange(
      supplements.map(
        (s): T =>
          s.supplement_id === id ? { ...s, selected: !s.selected, quantity: s.selected ? undefined : s.quantity } : s,
      ),
    );
  };
  const setQuantity = (id: number, value: number): void => {
    onChange(supplements.map((s): T => (s.supplement_id === id ? { ...s, quantity: value } : s)));
  };

  return (
    <div className="bt-supplements">
      <span className="bt-supplements__label">{labels.supplements_label}</span>
      {visible.map((s) => (
        <div
          key={s.supplement_id}
          className={`bt-supplement ${s.selected ? 'bt-supplement--selected' : ''}`}
          role="button"
          tabIndex={0}
          onClick={() => toggle(s.supplement_id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggle(s.supplement_id);
            }
          }}
        >
          <div className="bt-supplement__info">
            <span className="bt-supplement__name">{s.name}</span>
            <span className="bt-supplement__detail">
              {s.price != null && s.price > 0 && `+${s.price_string ?? formatPrice(s.price)}`}
              {s.duration_minutes != null && s.duration_minutes > 0 && ` · +${s.duration_minutes} min`}
            </span>
          </div>
          <div className="bt-supplement__controls">
            {quantity && s.selected && s.quantity_selectable === true && (
              // Le stepper vit dans la carte cliquable : ses clics ne doivent pas décocher le supplément.
              <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
                <Stepper
                  value={s.quantity ?? quantity.defaultQuantity}
                  onChange={(value: number): void => setQuantity(s.supplement_id, value)}
                  min={1}
                  max={quantity.max}
                  labels={{
                    decrease: `${quantity.labels.decrease} ${s.name}`,
                    increase: `${quantity.labels.increase} ${s.name}`,
                  }}
                />
              </div>
            )}
            <button
              type="button"
              className={`bt-supplement__toggle ${s.selected ? 'bt-supplement__toggle--on' : ''}`}
              aria-label={s.selected ? `${labels.remove_supplement} ${s.name}` : `${labels.add_supplement} ${s.name}`}
            />
          </div>
        </div>
      ))}
      {hiddenCount > 0 && (
        <button type="button" className="bt-supplements__more" onClick={(): void => setExpanded(true)}>
          {supplementsShowMoreLabel(hiddenCount, { one: labels.show_more_one, other: labels.show_more_other })}
        </button>
      )}
    </div>
  );
}
